'use client';
import 'dayjs/locale/es';
import 'dayjs/locale/ca';
import { Button, Form } from '@mage-ui/components';
import { cx } from '@mage-ui/styled-system/css';
import { Flex } from '@mage-ui/styled-system/jsx';
import { gridCol } from '@mage-ui/styled-system/recipes';
import { useContext, useEffect, useRef, useState } from 'react';
import { useCart } from '@/components/context/CartProvider';
import { StaticTextContext } from '@/components/context/StaticTextProvider';
import { AlertItem } from '@/components/ui/feedback/alert-item/AlertItem';
import { GridCol } from '@/components/ui/layouts/grids/GridCol';
import { GridRow } from '@/components/ui/layouts/grids/GridRow';
import { createRedsysForm } from '@/services/client/redsys-service';
import { request } from '@/services/server/request';
import { Section } from '../../layouts/sections/Section';
import { rules } from './rules';

export const FormShop = ({ locale }) => {
	const { st } = useContext(StaticTextContext);
	const { items, clearCart } = useCart();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [backendErrors, setBackendErrors] = useState({});
	const alertRef = useRef(null);

	useEffect(() => {
		if (backendErrors['root.serverError']) {
			alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, [backendErrors]);

	const setError = (field, error) => {
		setBackendErrors((prev) => ({
			...prev,
			[field]: error,
		}));
	};

	const onSubmit = async (values) => {
		setIsSubmitting(true);
		setBackendErrors({});

		const groupItems = () => {
			const map = items.reduce((acc, item) => {
				if (acc[item.id]) {
					acc[item.id].quantity += 1;
				} else {
					acc[item.id] = {
						id: item.id,
						quantity: 1,
					};
				}
				return acc;
			}, {});

			return Object.values(map);
		};

		const response = await request('/api/order', 'POST', {
			...values,
			dossiers: groupItems(),
			locale,
		});

		await handleResponse(response);
		setIsSubmitting(false);
	};

	const externalErrors = Object.fromEntries(
		Object.entries(backendErrors).map(([field, error]) => [
			field,
			{
				type: error.type ?? 'server',
				message: Array.isArray(error.message)
					? error.message.map((message) => st(message)).join(', ')
					: st(error.message),
			},
		]),
	);

	const handleResponse = ({ status, data }) => {
		switch (status) {
			case 200:
			case 201:
			case 204:
				clearCart();
				createRedsysForm(data.body.redsysData).submit();
				break;

			case 422:
			case 400:
			case 401:
			case 403:
			case 404:
			case 500:
				if (status === 422) {
					handleValidationError(data);
				} else {
					handleServerError(data);
				}
				break;

			default:
				break;
		}

		setIsSubmitting(false);
	};

	const handleValidationError = (data) => {
		const { body } = data;
		for (const field of Object.keys(body)) {
			setError(field, {
				type: 'server',
				message: body?.[field],
			});
		}
		setError('root.serverError', {
			message: data?.details ?? st('validation.server_error'),
		});
	};

	const handleServerError = (data) => {
		setError('root.serverError', {
			message: data?.details ?? st('validation.server_error'),
		});
	};

	return (
		<Section>
			{backendErrors['root.serverError'] && (
				<AlertItem
					ref={alertRef}
					text={backendErrors['root.serverError'].message}
				/>
			)}
			<Form
				externalErrors={externalErrors}
				onSubmit={onSubmit}
				rules={rules({ st })}
			>
				<Flex direction='column' gap='24'>
					<GridRow>
						<GridCol className={cx(gridCol({ colSpanM: '6', colSpanL: '4' }))}>
							<Form.InputText
								label={st('forms.shop.name.label')}
								name='name'
								withAsterisk={true}
							/>
						</GridCol>

						<GridCol className={cx(gridCol({ colSpanM: '6', colSpanL: '4' }))}>
							<Form.InputText
								label={st('forms.shop.surnames.label')}
								name='surnames'
								withAsterisk={true}
							/>
						</GridCol>
					</GridRow>
					<GridRow>
						<GridCol className={cx(gridCol({ colSpanM: '6', colSpanL: '4' }))}>
							<Form.InputText
								label={st('forms.shop.email.label')}
								name='email'
								withAsterisk={true}
							/>
						</GridCol>

						<GridCol className={cx(gridCol({ colSpanM: '6', colSpanL: '4' }))}>
							<Form.InputText
								label={st('forms.shop.confirm_email.label')}
								name='email_confirmation'
								withAsterisk={true}
							/>
						</GridCol>
					</GridRow>
					<GridRow>
						<GridCol className={cx(gridCol({ colSpanM: '3', colSpanL: '3' }))}>
							{/* should be data policy checkbox? */}
							<Button type='submit' loading={isSubmitting}>
								{st('forms.shop.submit.label')}
							</Button>
						</GridCol>
					</GridRow>
				</Flex>
			</Form>
		</Section>
	);
};
